import { SignIn } from "@/components/signin";
import { ButtonLink } from "@/components/ui/button-link";
import { trackNavItem } from "@/utils/analytics/track-navigation";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import React from "react";
import { MenuItem } from "../homepage/types";

// WhatsApp icon component
const WhatsAppIcon = ({ className }: { className?: string }) => (
	<svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
		<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488" />
	</svg>
);

interface DesktopMenuProps {
	menuItems: MenuItem[];
	extraMenuItems: MenuItem[];
}

export const DesktopMenu: React.FC<DesktopMenuProps> = ({ menuItems, extraMenuItems }) => {
	const handleWhatsAppClick = () => {
		// Track WhatsApp navigation
		trackNavItem({ action: "whatsapp", category: "contact" });
	};

	return (
		<div className="flex items-center gap-8">
			{/* Navigation Links */}
			<nav className="flex items-center space-x-6">
				<Link
					href="https://api.whatsapp.com/send?phone=919590039003"
					onClick={handleWhatsAppClick}
					target="_blank"
					title="Chat with us on Whatsapp"
					aria-label="Chat with us on Whatsapp"
					rel="noopener noreferrer"
					className="inline-block btn-clickable text-small font-medium text-primary-700 hover:text-primary-700 relative after:absolute after:-bottom-2 after:left-1/2 after:h-0.5 after:bg-primary after:rounded-full after:transition-all after:duration-300 after:w-0 after:opacity-50 hover:after:w-full hover:after:left-0"
				>
					<WhatsAppIcon className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
				</Link>

				{menuItems.map(({ href, active, hasDropdown, label, ...linkProps }) => {
					const handleNavClick = () => {
						// Track navigation item click
						trackNavItem({
							action: label.toLowerCase().replace(/\s+/g, "-"),
							category: "navigation",
						});
					};

					return (
						<Link
							key={href}
							href={href}
							aria-current={active ? "page" : undefined}
							aria-haspopup={hasDropdown ? "true" : undefined}
							aria-expanded={hasDropdown ? "false" : undefined}
							className={`inline-block btn-clickable text-small font-medium text-gray-800 hover:text-primary-700 relative after:absolute after:-bottom-2 after:left-1/2 after:h-0.5 after:bg-primary after:rounded-full after:transition-all after:duration-300 after:w-0 after:opacity-50 hover:after:w-full hover:after:left-0 ${
								active ? "text-primary-700" : ""
							}`}
							onClick={handleNavClick}
							{...linkProps}
						>
							<div className="flex items-center gap-1">
								{label}
								{hasDropdown && <ChevronDown className="h-3 w-3 text-gray-500" aria-hidden="true" />}
							</div>
						</Link>
					);
				})}
			</nav>

			{/*
				Renders action buttons based on user authentication and role:
				- "Post Property Free": Shows for non-authenticated users and sellers without properties
				- "Dashboard": Shows for sellers with properties (seller dashboard) or buyers (buyer dashboard)
				The extraMenuItems prop is populated by getExtraMenuItems() in header/index.tsx
			*/}
			<div className="flex items-center gap-3">
				{extraMenuItems.map(({ href, label, icon: Icon, ...linkProps }) => {
					const handleExtraMenuClick = () => {
						// Track extra menu item click
						trackNavItem({
							action: label.toLowerCase().replace(/\s+/g, "-"),
							category: "cta",
						});
					};

					return (
						<div key={href} className="flex items-center gap-3">
							<ButtonLink
								variant="secondary"
								href={href}
								className="data-[hovered]:-translate-y-0.5 w-44 justify-center"
								aria-label={label}
								onClick={handleExtraMenuClick}
								{...linkProps}
							>
								<span className="relative z-10 flex items-center gap-1.5">
									{Icon && (
										<Icon
											className="h-4 w-4 transition-transform duration-200 group-hover:scale-110"
											aria-hidden="true"
										/>
									)}
									<span className="transition-colors duration-200">{label}</span>
								</span>
							</ButtonLink>
						</div>
					);
				})}

				{/* Log in Component */}
				<SignIn />
			</div>
		</div>
	);
};
